import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getInventoryCategories } from '../../data/mockApi';

const { FiPackage, FiAlertTriangle, FiTrendingUp, FiTrendingDown } = FiIcons;

// Dark theme category colors
const CATEGORY_COLORS = {
  emerald: { bg: 'bg-lime-500/10', icon: 'text-lime-400', border: 'border-lime-500/20' },
  blue: { bg: 'bg-sky-500/10', icon: 'text-sky-400', border: 'border-sky-500/20' },
  purple: { bg: 'bg-violet-500/10', icon: 'text-violet-400', border: 'border-violet-500/20' },
  amber: { bg: 'bg-amber-500/10', icon: 'text-amber-400', border: 'border-amber-500/20' },
};

const InventoryOverview = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = await getInventoryCategories();
      setCategories(data);
      setLoading(false);
    };
    fetchData();
  }, []);

  const getColorClasses = (color) => CATEGORY_COLORS[color] || CATEGORY_COLORS.emerald;

  return (
    <motion.div
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.2, duration: 0.4 }}
      className="bg-[#111916] rounded-xl border border-[#243129]"
    >
      <div className="p-5 border-b border-[#243129]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiPackage} className="w-5 h-5 text-[#84cc16]" />
            <h3 className="font-display text-lg font-semibold text-[#e8f5e9]">Inventory Overview</h3>
          </div>
          <span className="text-xs font-mono text-[#4a5f50]">By Category</span>
        </div>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="text-center text-[#6b8772] font-mono text-sm py-4">Loading overview...</div>
        ) : (
          <div className="space-y-2">
            {categories.map((category, index) => {
              const colors = getColorClasses(category.color);
              const isUp = category.trend === 'up';
              return (
                <motion.div
                  key={category.name}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-3 bg-[#0a0f0d] border border-[#1c2822] rounded-lg hover:border-[#243129] transition-colors group cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors.bg} border ${colors.border}`}>
                      <SafeIcon icon={FiPackage} className={`w-4 h-4 ${colors.icon}`} />
                    </div>
                    <div>
                      <h4 className="font-mono font-medium text-sm text-[#e8f5e9] group-hover:text-[#84cc16] transition-colors">{category.name}</h4>
                      <div className="flex items-center gap-2 text-xs font-mono text-[#6b8772]">
                        <span>{category.totalItems} items</span>
                        {category.lowStock > 0 && (
                          <>
                            <span className="text-[#4a5f50]">•</span>
                            <div className="flex items-center gap-1 text-amber-400">
                              <SafeIcon icon={FiAlertTriangle} className="w-3 h-3" />
                              <span>{category.lowStock} low</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded ${isUp ? 'bg-lime-500/10' : 'bg-red-500/10'}`}>
                    <SafeIcon
                      icon={isUp ? FiTrendingUp : FiTrendingDown}
                      className={`w-3.5 h-3.5 ${isUp ? 'text-lime-400' : 'text-red-400'}`}
                    />
                    <span className={`text-xs font-mono font-medium ${isUp ? 'text-lime-400' : 'text-red-400'}`}>
                      {category.change}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className="w-full mt-4 text-xs font-mono font-medium text-[#84cc16] hover:text-lime-300 transition-colors py-2 border border-[#243129] rounded-lg hover:border-[#84cc16]/30 hover:bg-lime-500/5"
        >
          View Full Inventory
        </motion.button>
      </div>
    </motion.div>
  );
};

export default InventoryOverview;
